// App.js
import React, { useState, useRef } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Paper,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import HistoryIcon from '@mui/icons-material/History';
// Monaco Editor component (you'll need to install @monaco-editor/react)
import Editor from '@monaco-editor/react';


// Dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});


const MySQLEditor  = () => {
  const [query, setQuery] = useState('SELECT * FROM users LIMIT 100;');
  const [results, setResults] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [executionTime, setExecutionTime] = useState(0);
  const [queryHistory, setQueryHistory] = useState([]);
  const editorRef = useRef(null);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    
    // SQL syntax highlighting
    monaco.languages.register({ id: 'mysql' });
    monaco.languages.setMonarchTokensProvider('mysql', {
      keywords: [
        'SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE', 'CREATE',
        'ALTER', 'DROP', 'TABLE', 'DATABASE', 'PROCEDURE', 'FUNCTION',
        'TRIGGER', 'VIEW', 'INDEX', 'JOIN', 'LEFT', 'RIGHT', 'INNER',
        'OUTER', 'ON', 'GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT',
        'OFFSET', 'UNION', 'ALL', 'DISTINCT', 'AS', 'AND', 'OR', 'NOT',
        'IN', 'BETWEEN', 'LIKE', 'IS', 'NULL', 'TRUE', 'FALSE'
      ],
      operators: [
        '=', '>', '<', '>=', '<=', '<>', '!=', '+', '-', '*', '/', '%'
      ],
      symbols: /[=><!~?:&|+\-*\/\^%]+/,
      tokenizer: {
        root: [
          [/[a-z_$][\w$]*/, {
            cases: {
              '@keywords': 'keyword',
              '@default': 'identifier'
            }
          }],
          [/[0-9]+/, 'number'],
          [/".*?"/, 'string'],
          [/'.*?'/, 'string'],
          [/\/\*/, 'comment', '@comment'],
          [/--.*$/, 'comment'],
        ],
        comment: [
          [/[^\/*]+/, 'comment'],
          [/\/\*/, 'comment', '@push'],
          ["\\*/", 'comment', '@pop'],
          [/[\/*]/, 'comment']
        ]
      }
    });
  };

  const executeQuery = async () => {
    if (!query.trim()) return;

    setLoading(true);
    setError('');
    const startTime = performance.now();

    try {
      const response = await fetch('/api/query/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query.trim(),
          isStoredProcedure: query.trim().toUpperCase().startsWith('CREATE PROCEDURE') || 
                            query.trim().toUpperCase().startsWith('CREATE FUNCTION')
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to execute query');
      }

      if (data.results && Array.isArray(data.results)) {
        // For SELECT queries
        if (data.results.length > 0) {
          const firstRow = data.results[0];
          const generatedColumns = Object.keys(firstRow).map(key => ({
            field: key,
            headerName: key.toUpperCase(),
            width: 150,
            editable: false,
          }));
          setColumns(generatedColumns);
          setResults(data.results);
        } else {
          setColumns([]);
          setResults([]);
        }
      } else if (data.message) {
        // For non-SELECT queries
        setColumns([]);
        setResults([]);
        setError(data.message);
      }

      // Add to history
      setQueryHistory(prev => [{
        id: Date.now(),
        query: query.trim(),
        timestamp: new Date().toLocaleString(),
        success: true
      }, ...prev.slice(0, 9)]);

    } catch (err) {
      setError(err.message);
      setColumns([]);
      setResults([]);
    } finally {
      setLoading(false);
      const endTime = performance.now();
      setExecutionTime(endTime - startTime);
    }
  };

  const clearEditor = () => {
    setQuery('');
    setError('');
    setResults([]);
    setColumns([]);
  };

  const saveQuery = () => {
    // Save query to local storage or send to backend
    const savedQueries = JSON.parse(localStorage.getItem('savedQueries') || '[]');
    savedQueries.push({
      id: Date.now(),
      query: query,
      name: `Query_${new Date().toLocaleString()}`,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('savedQueries', JSON.stringify(savedQueries));
    alert('Query saved successfully!');
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <AppBar position="static" elevation={0}>
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              MySQL Web Editor
            </Typography>
            <Button color="inherit">Connect Database</Button>
          </Toolbar>
        </AppBar>

        <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
          <Grid container spacing={2}>
            {/* Editor Section */}
            <Grid item xs={12} md={8}>
              <Card sx={{ height: '500px' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">SQL Editor</Typography>
                    <Box>
                      <Tooltip title="Execute (Ctrl+Enter)">
                        <IconButton onClick={executeQuery} color="primary" disabled={loading}>
                          <PlayArrowIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Save Query">
                        <IconButton onClick={saveQuery} color="secondary">
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clear Editor">
                        <IconButton onClick={clearEditor}>
                          <ClearIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  <Box sx={{ flexGrow: 1, border: '1px solid #333', borderRadius: 1 }}>
                    <Editor
                      height="100%"
                      defaultLanguage="mysql"
                      value={query}
                      onChange={setQuery}
                      onMount={handleEditorDidMount}
                      options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        lineNumbers: 'on',
                        roundedSelection: false,
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 2,
                        wordWrap: 'on',
                      }}
                      theme="vs-dark"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* History Section */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '500px' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom>
                    <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Query History
                  </Typography>
                  <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    {queryHistory.map((item) => (
                      <Paper
                        key={item.id}
                        sx={{
                          p: 1,
                          mb: 1,
                          cursor: 'pointer',
                          backgroundColor: item.success ? '#1b5e20' : '#c62828',
                          '&:hover': { backgroundColor: item.success ? '#2e7d32' : '#d32f2f' }
                        }}
                        onClick={() => setQuery(item.query)}
                      >
                        <Typography variant="body2" noWrap title={item.query}>
                          {item.query}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.timestamp}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Results Section */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                      Results {results.length > 0 && `(${results.length} rows)`}
                    </Typography>
                    {executionTime > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Execution time: {executionTime.toFixed(2)}ms
                      </Typography>
                    )}
                  </Box>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : results.length > 0 ? (
                    <Box sx={{ height: 400, width: '100%' }}>
                      <DataGridPro
                        rows={results}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        checkboxSelection
                        disableSelectionOnClick
                        experimentalFeatures={{ newEditingApi: true }}
                      />
                    </Box>
                  ) : (
                    !error && (
                      <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                          No results to display. Execute a query to see results.
                        </Typography>
                      </Paper>
                    )
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default MySQLEditor;